import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { AlertCircle, CheckCircle2, Clock, X } from "lucide-react";
import {
  ANCHORS,
  ARC,
  BUBBLE,
  CHRIS_EVENTS,
  HOME,
  MESSAGE_TEXT,
  POSE_SLUGS,
  READY_POSE,
  SIZES,
  srcFor,
  TARGET,
  TARGET_TESTID,
  TIMINGS,
  TRIGGER,
  WALK_FRAMES,
  Z_INDEX,
  LOGO_DESKTOP_TESTID,
  LOGO_MOBILE_TESTID,
  SIDEBAR_TESTID,
} from "./chrisConfig";

const easeInOut = (t) => (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2);
const rectOf = (id) => {
  const el = document.querySelector(`[data-testid="${id}"]`);
  if (!el) return null;
  const r = el.getBoundingClientRect();
  return r.width > 0 && r.height > 0 ? r : null;
};

// ---------------------------------------------------------------------------
// C4 „Chris auf Abruf" (Okt 2026) — reine Anzeige-Helfer (keine Fachlogik).
// ---------------------------------------------------------------------------
const C4_AREA_LABEL = {
  wirksamkeit: "Wirksamkeitskontrolle",
  fuehrerschein: "Führerscheinkontrolle",
  handschutzplan: "Hautschutzplan",
  unterweisung: "Unterweisung",
};
const c4NowText = (key, count) => {
  const n = count || 1;
  switch (key) {
    case "EV-012":
      return n === 1
        ? "Eine Wirksamkeitskontrolle wartet auf Ihre Prüfung."
        : `${n} Wirksamkeitskontrollen warten auf Ihre Prüfung.`;
    case "EV-020_021":
      return n === 1
        ? "Eine Führerscheinkontrolle wartet auf Ihre Entscheidung."
        : `${n} Führerscheinkontrollen warten auf Ihre Entscheidung.`;
    case "EV-032":
      return n === 1
        ? "Der Hautschutzplan sollte wieder geprüft werden."
        : `${n} Hautschutzpläne sollten wieder geprüft werden.`;
    case "EV-003":
      return n === 1
        ? "Ein Mitarbeiter hat überfällige Unterweisungen."
        : `${n} Mitarbeiter haben überfällige Unterweisungen.`;
    default:
      return "";
  }
};
const c4RelPast = (d) => (d <= 0 ? "heute" : d === 1 ? "gestern" : `vor ${d} Tagen`);
const c4RelFuture = (d) => (d <= 0 ? "heute" : d === 1 ? "morgen" : `in ${d} Tagen`);

// Chris – der CLartext-Helfer · C1/C3 visuelle Overlay-Schicht.
// Rein visuelle Overlay-Schicht. Keine Fachlogik, kein Backend, kein Schalter.
export default function ChrisHelper() {
  const location = useLocation();
  const overlayRef = useRef(null);
  const containerRef = useRef(null);

  const [pose, setPoseState] = useState("01_chris_home_sit");
  const [facing, setFacing] = useState(1);
  const [bubble, setBubble] = useState(false);
  const [bubblePos, setBubblePos] = useState({ left: -9999, top: -9999, side: "down", tailMain: 20, tailCross: 20 });
  const [devTrigger, setDevTrigger] = useState(false);
  const [message, setMessage] = useState(MESSAGE_TEXT);

  // C4 „Auf Abruf" — Zustand der manuellen Zusammenfassung (additiv zu C3).
  const [c4Open, setC4Open] = useState(false);
  const [c4Loading, setC4Loading] = useState(false);
  const [c4Error, setC4Error] = useState(false);
  const [c4Data, setC4Data] = useState(null);
  const [c4Pos, setC4Pos] = useState({ left: 24, top: 120 });
  const [isDesktop, setIsDesktop] = useState(
    typeof window !== "undefined" ? window.innerWidth >= SIZES.breakpointLg : true
  );

  // C3: dynamisches Ziel/Modus/Quittung je ausgewähltem Event (statt Einzelkonstante).
  const targetTestIdRef = useRef(TARGET_TESTID);
  const modeRef = useRef("perch");
  const onShownRef = useRef(null);

  const bubbleRef = useRef(null);
  const imgRef = useRef(null);
  const facingRef = useRef(1);
  const phaseRef = useRef("home_sit");
  const poseKeyRef = useRef("01_chris_home_sit");
  const pointRef = useRef({ x: -9999, y: -9999 });
  const sizeRef = useRef({ w: 140, h: 210 });
  const runIdRef = useRef(0);
  const activeRef = useRef(false);
  const rafRef = useRef(null);
  const frameTimerRef = useRef(null);
  const timeoutsRef = useRef([]);
  const mountedRef = useRef(true);
  // C3-Fix: Zeitfenster, in dem Chris' EIGENES programmatisches scrollIntoView
  // (mobiler Führerschein-Nav-Streifen) NICHT als Nutzer-Scroll gewertet wird.
  const scrollSuppressUntilRef = useRef(0);
  // C4: Spiegel-Ref des Offen-Zustands für Nicht-React-Closures (Klick-Gate/Arbitrierung).
  const c4OpenRef = useRef(false);

  const computeSize = () => {
    const h =
      window.innerWidth >= SIZES.breakpointLg ? SIZES.desktopHeight : SIZES.mobileHeight;
    sizeRef.current = { w: h * SIZES.ratio, h };
  };

  const getTargetRect = () => rectOf(targetTestIdRef.current);

  // C3-Fix: Chris' EIGENES scrollIntoView (mobiler Führerschein-Nav-Streifen) darf
  // die laufende Sequenz NICHT abbrechen. Vor dem Scroll wird ein kurzes Zeitfenster
  // geöffnet, in dem der globale Scroll-Abbruch dieses (programmatische) Scroll-Event
  // ignoriert. Nutzer-Scrolls außerhalb des Fensters brechen weiterhin ab.
  const scrollTargetIntoView = (testId) => {
    const el = document.querySelector(`[data-testid="${testId}"]`);
    if (!el || !el.scrollIntoView) return;
    scrollSuppressUntilRef.current = performance.now() + TRIGGER.scrollSuppressMs;
    try {
      el.scrollIntoView({ inline: "nearest", block: "nearest" });
    } catch (_) {
      /* noop */
    }
  };

  // Cooldown-Quittung EINMAL feuern, sobald die Sprechblase sichtbar wurde.
  const fireShown = () => {
    const fn = onShownRef.current;
    onShownRef.current = null;
    if (fn) fn();
  };

  const applyTransform = () => {
    const c = containerRef.current;
    if (!c) return;
    const a = ANCHORS[poseKeyRef.current] || { ax: 0.5, ay: 0.83 };
    const { w, h } = sizeRef.current;
    const p = pointRef.current;
    c.style.width = `${w}px`;
    c.style.height = `${h}px`;
    c.style.transform = `translate(${p.x - a.ax * w}px, ${p.y - a.ay * h}px)`;
  };

  // Pose-Wechsel: Bildquelle SYNCHRON imperativ setzen (kein React-Render-Lag),
  // damit Pose-Bild und Position/Transform immer im selben Tick übereinstimmen.
  // Zusätzlich State für data-pose/React-Konsistenz.
  const setPose = (key) => {
    poseKeyRef.current = key;
    const img = imgRef.current;
    if (img) img.src = srcFor(key);
    setPoseState(key);
    applyTransform();
  };

  // Blickrichtung ebenfalls synchron am Bild setzen (verhindert Flip-Lag).
  const setFace = (dir) => {
    facingRef.current = dir;
    const img = imgRef.current;
    if (img) img.style.transform = `scaleX(${dir})`;
    setFacing(dir);
  };

  const placeHome = () => {
    const desktop = rectOf(LOGO_DESKTOP_TESTID);
    const r = desktop || rectOf(LOGO_MOBILE_TESTID);
    if (!r) return false;
    const { w } = sizeRef.current;
    const a = ANCHORS["01_chris_home_sit"] || { ax: 0.5, ay: 0.16 };
    if (desktop) {
      // Desktop-Stammplatz: rechts NEBEN dem Logo im weißen Sidebar-Kopfbereich.
      // Kopf-Oberkante ~ Logo-Oberkante; in der Sidebar geklemmt, damit Chris
      // weder Logo noch Unternehmensnamen verdeckt.
      let x = desktop.right + HOME.desktopDx + a.ax * w;
      const sidebar = rectOf(SIDEBAR_TESTID);
      if (sidebar) {
        const maxX = sidebar.right - HOME.margin - (1 - a.ax) * w;
        if (x > maxX) x = maxX;
      }
      pointRef.current = { x, y: desktop.top + HOME.desktopHeadDy };
    } else {
      // Mobile/Tablet: unverändert am mobilen Logo-Icon.
      pointRef.current = {
        x: r.left + r.width * HOME.mobileFracX,
        y: r.bottom + HOME.mobileDyHead,
      };
    }
    phaseRef.current = "home_sit";
    setFace(1);
    setPose("01_chris_home_sit");
    return true;
  };

  // Zielposition relativ zum GEMESSENEN Ziel-Element. Zwei Modi:
  //  - "above": Chris STEHT über dem Ziel (Füße knapp über der Oberkante,
  //    horizontal zentriert) und zeigt nach unten (Sidebar-Menüpunkt).
  //  - "perch": Chris SITZT auf der oberen Zielkante (eingefrorenes WK-Verhalten).
  // Reicht der Platz über dem Ziel nicht (Top-Clipping), fällt "above" auf
  // "perch" zurück (Betreiber-Fallback: dann auf den Eintrag setzen).
  const computeTargetPoint = (btn, mode) => {
    const { w, h } = sizeRef.current;
    const m = TARGET.margin;
    const vw = window.innerWidth;
    if (mode === "above") {
      const a = ANCHORS["08_chris_point"] || { ax: 0.52, ay: 0.17 };
      let x = btn.left + btn.width / 2;
      const minX = m + a.ax * w;
      const maxX = vw - m - (1 - a.ax) * w;
      if (maxX >= minX) x = Math.min(Math.max(x, minX), maxX);
      const y = btn.top - TARGET.aboveGap - (1 - a.ay) * h;
      if (y >= TARGET.minHeadY) return { x, y, mode: "above" };
      // zu wenig Platz oben → Sitz-Fallback (unten)
    }
    const a = ANCHORS["07_chris_target_sit"] || { ax: 0.5, ay: 0.14 };
    // Horizontal: schmale Ziele (z. B. Mobile-Nav-Icon) mittig, breite Buttons
    // wie bisher per Inset — WK-Verhalten bleibt dadurch unverändert.
    let x = btn.left + Math.min(TARGET.perchInsetX, btn.width * 0.5);
    const minX = m + a.ax * w;
    const maxX = vw - m - (1 - a.ax) * w;
    if (maxX >= minX) x = Math.min(Math.max(x, minX), maxX);
    // Vertikal: Gesäß (seatFrac) liegt auf der OBEREN Kante. pointRef.y ist die
    // Kopf-Oberkante → Kopf um (seatFrac - ay) höher als die Kante setzen.
    let y = btn.top - (TARGET.seatFrac - a.ay) * h;
    if (y < TARGET.minHeadY) y = TARGET.minHeadY; // kein Top-Clipping
    return { x, y, mode: "perch" };
  };

  const delay = (ms) =>
    new Promise((res) => {
      const id = setTimeout(res, ms);
      timeoutsRef.current.push(id);
    });

  // Sprechblase vollständig in den Viewport klemmen (horizontal + vertikal).
  // Registrierungspunkt = Kopf (pointRef); die Blase sitzt über dem Kopf.
  // Klassische Comic-Sprechblase: Platzierung robust in den Viewport. Bevorzugt
  // ÜBER Chris (Schwanz nach unten); ist oben kein Platz (Chris sitzt hoch), dann
  // LINKS neben Chris (Schwanz nach rechts), sonst RECHTS. Schwanz zeigt immer auf Chris.
  const positionBubble = () => {
    const el = bubbleRef.current;
    if (!el) return;
    const bw = el.offsetWidth;
    const bh = el.offsetHeight;
    const p = pointRef.current;
    const a = ANCHORS[poseKeyRef.current] || { ax: 0.5, ay: 0.15 };
    const { w, h } = sizeRef.current;
    const m = TARGET.margin;
    const gap = BUBBLE.gap;
    const t = BUBBLE.tailSize;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const figLeft = p.x - a.ax * w;
    const figRight = p.x + (1 - a.ax) * w;
    const headTop = p.y; // pointRef = Kopf-Oberkante
    const mouthY = p.y + 0.1 * h; // grob Kopf/Mund
    const clampX = (x) => Math.min(Math.max(x, m), Math.max(m, vw - m - bw));
    const clampY = (y) => Math.min(Math.max(y, m), Math.max(m, vh - m - bh));
    let side, left, top, tailMain = 0, tailCross = 0;
    if (headTop - m >= bh + gap + t) {
      side = "down";
      left = clampX(p.x - bw / 2);
      top = headTop - gap - t - bh;
      tailMain = Math.min(Math.max(p.x - left, 20), Math.max(20, bw - 20));
    } else if (figLeft - m >= bw + gap + t) {
      side = "right";
      left = figLeft - gap - t - bw;
      top = clampY(mouthY - bh / 2);
      tailCross = Math.min(Math.max(mouthY - top, 18), Math.max(18, bh - 18));
    } else {
      side = "left";
      left = Math.min(figRight + gap + t, vw - m - bw);
      top = clampY(mouthY - bh / 2);
      tailCross = Math.min(Math.max(mouthY - top, 18), Math.max(18, bh - 18));
    }
    setBubblePos({ left, top, side, tailMain, tailCross });
  };

  const showBubble = () => setBubble(true);
  const hideBubble = () => setBubble(false);

  // Zwei CSS-Dreiecke (Navy-Kontur außen, Weiß innen) als Sprechblasen-Schwanz,
  // je nach Platzierung nach unten/rechts/links zeigend (immer auf Chris).
  const tailPair = () => {
    const t = BUBBLE.tailSize;
    const bwid = BUBBLE.borderWidth;
    const navy = "#1a3f6f";
    const white = "#ffffff";
    const { side, tailMain, tailCross } = bubblePos;
    const base = { position: "absolute", width: 0, height: 0 };
    if (side === "down") {
      return [
        { ...base, top: "100%", left: tailMain, transform: "translateX(-50%)", borderLeft: `${t}px solid transparent`, borderRight: `${t}px solid transparent`, borderTop: `${t}px solid ${navy}` },
        { ...base, top: `calc(100% - ${bwid + 0.5}px)`, left: tailMain, transform: "translateX(-50%)", borderLeft: `${t - bwid}px solid transparent`, borderRight: `${t - bwid}px solid transparent`, borderTop: `${t - bwid}px solid ${white}` },
      ];
    }
    if (side === "right") {
      return [
        { ...base, left: "100%", top: tailCross, transform: "translateY(-50%)", borderTop: `${t}px solid transparent`, borderBottom: `${t}px solid transparent`, borderLeft: `${t}px solid ${navy}` },
        { ...base, left: `calc(100% - ${bwid + 0.5}px)`, top: tailCross, transform: "translateY(-50%)", borderTop: `${t - bwid}px solid transparent`, borderBottom: `${t - bwid}px solid transparent`, borderLeft: `${t - bwid}px solid ${white}` },
      ];
    }
    return [
      { ...base, right: "100%", top: tailCross, transform: "translateY(-50%)", borderTop: `${t}px solid transparent`, borderBottom: `${t}px solid transparent`, borderRight: `${t}px solid ${navy}` },
      { ...base, right: `calc(100% - ${bwid + 0.5}px)`, top: tailCross, transform: "translateY(-50%)", borderTop: `${t - bwid}px solid transparent`, borderBottom: `${t - bwid}px solid transparent`, borderRight: `${t - bwid}px solid ${white}` },
    ];
  };

  const clearMotion = () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    if (frameTimerRef.current) clearInterval(frameTimerRef.current);
    rafRef.current = null;
    frameTimerRef.current = null;
    timeoutsRef.current.forEach(clearTimeout);
    timeoutsRef.current = [];
  };

  const stop = () => {
    runIdRef.current += 1; // invalidiert laufende Sequenz
    clearMotion();
    activeRef.current = false;
    hideBubble();
  };

  const walk = (from, to, dir) =>
    new Promise((resolve) => {
      const myId = runIdRef.current;
      const dist = Math.hypot(to.x - from.x, to.y - from.y);
      const dur = Math.max(300, (dist / TIMINGS.walkSpeed) * 1000);
      const arc = Math.min(ARC.maxPx, dist * ARC.fraction);
      setFace(dir === "toHome" ? -1 : 1);
      let fi = 0;
      setPose(WALK_FRAMES[0]);
      frameTimerRef.current = setInterval(() => {
        fi = (fi + 1) % WALK_FRAMES.length;
        setPose(WALK_FRAMES[fi]);
      }, TIMINGS.walkFrameInterval);
      const start = performance.now();
      const tick = (now) => {
        if (runIdRef.current !== myId || !mountedRef.current) return;
        let t = (now - start) / dur;
        if (t > 1) t = 1;
        const e = easeInOut(t);
        pointRef.current = {
          x: from.x + (to.x - from.x) * e,
          y: from.y + (to.y - from.y) * e - Math.sin(Math.PI * t) * arc,
        };
        applyTransform();
        if (t < 1) {
          rafRef.current = requestAnimationFrame(tick);
        } else {
          if (frameTimerRef.current) clearInterval(frameTimerRef.current);
          frameTimerRef.current = null;
          resolve();
        }
      };
      rafRef.current = requestAnimationFrame(tick);
    });

  const finishReset = () => {
    clearMotion();
    hideBubble();
    activeRef.current = false;
    placeHome();
  };

  const runFull = async (myId) => {
    const alive = () => runIdRef.current === myId && mountedRef.current;
    activeRef.current = true;

    // HOME_SIT — Chris sitzt still an seinem Stammplatz.
    if (!placeHome()) {
      activeRef.current = false;
      return;
    }
    const homePt = { ...pointRef.current };
    phaseRef.current = "home_sit";
    await delay(TIMINGS.homeSettle);
    if (!alive()) return;
    if (!getTargetRect()) return finishReset();

    // STAND_UP — auf der STELLE aufstehen. KEINE Translation Richtung Ziel.
    phaseRef.current = "stand_up";
    pointRef.current = { ...homePt };
    setPose("02_chris_stand_up");
    await delay(TIMINGS.standUpDuration);
    if (!alive()) return;

    // READY_TO_WALK — VOLLSTÄNDIG AUFRECHT stehen (06_chris_arrive), kurz still:
    // „aufgestanden, steht aufrecht, gleich los". KEINE Ortsveränderung.
    phaseRef.current = "ready_to_walk";
    pointRef.current = { ...homePt };
    setFace(1);
    setPose(READY_POSE);
    await delay(TIMINGS.readyDuration);
    if (!alive()) return;

    // WALK_TO_TARGET — ERST JETZT Translation + Walk-Cycle gleichzeitig.
    phaseRef.current = "walk_to_target";
    const tp = computeTargetPoint(getTargetRect(), modeRef.current);
    const targetPt = { x: tp.x, y: tp.y };
    const resolvedMode = tp.mode;
    await walk(homePt, targetPt, "toTarget");
    if (!alive()) return;

    // ARRIVE — Stabilisierung, keine Seitwärtsbewegung.
    phaseRef.current = "arrive";
    pointRef.current = { ...targetPt };
    setPose("06_chris_arrive");
    await delay(TIMINGS.arriveDuration);
    if (!alive()) return;

    // TARGET_SIT — "perch": Gesäß auf der Zielkante; "above": aufrecht stehen.
    phaseRef.current = "target_sit";
    pointRef.current = { ...targetPt };
    setPose(resolvedMode === "above" ? "06_chris_arrive" : "07_chris_target_sit");
    await delay(TIMINGS.sitDuration);
    if (!alive()) return;

    // POINT + Sprechblase — gleiche Position, nur Pose. Sprechblase sichtbar →
    // ERST JETZT gilt das Event als "gezeigt" (Cooldown-Quittung).
    phaseRef.current = "point";
    pointRef.current = { ...targetPt };
    setPose("08_chris_point");
    showBubble();
    fireShown();
    await delay(TIMINGS.pointDuration);
    if (!alive()) return;
    await delay(TIMINGS.messageDuration);
    if (!alive()) return;
    hideBubble();

    // WAIT — ruhige Wartepose, gleiche Position.
    phaseRef.current = "wait";
    pointRef.current = { ...targetPt };
    setPose("09_chris_wait");
    await delay(TIMINGS.waitDuration);
    if (!alive()) return;

    // STAND_UP_RETURN — DIREKT am Ziel aufstehen. KEINE Translation Richtung Logo.
    phaseRef.current = "stand_up_return";
    pointRef.current = { ...targetPt };
    setPose("02_chris_stand_up");
    await delay(TIMINGS.returnStandUp);
    if (!alive()) return;

    // READY_TO_RETURN — VOLLSTÄNDIG AUFRECHT stehen (06), zur Heimat gewandt,
    // kurz still. KEINE Ortsveränderung Richtung Logo.
    phaseRef.current = "ready_to_return";
    pointRef.current = { ...targetPt };
    setFace(-1);
    setPose(READY_POSE);
    await delay(TIMINGS.readyDuration);
    if (!alive()) return;

    // WALK_HOME — ERST JETZT Rückbewegung.
    phaseRef.current = "walk_home";
    await walk(targetPt, homePt, "toHome");
    if (!alive()) return;

    // ARRIVE_HOME — Stabilisierung am Logo, kein „Nachgleiten".
    phaseRef.current = "arrive_home";
    pointRef.current = { ...homePt };
    setPose("06_chris_arrive");
    await delay(TIMINGS.arriveHomeDuration);
    if (!alive()) return;

    // HOME_SIT — erst DANACH wieder hinsetzen.
    placeHome();
    activeRef.current = false;
  };

  const runReduced = async (myId) => {
    const alive = () => runIdRef.current === myId && mountedRef.current;
    const btn = getTargetRect();
    if (!btn) {
      placeHome();
      return;
    }
    activeRef.current = true;
    // Reduced Motion: KEIN Laufweg, aber exakt dieselbe Ziel-Position wie im
    // vollen Zyklus. Sprechblase sichtbar → Cooldown-Quittung.
    const tp = computeTargetPoint(btn, modeRef.current);
    phaseRef.current = "point";
    pointRef.current = { x: tp.x, y: tp.y };
    setFace(1);
    setPose("08_chris_point");
    showBubble();
    fireShown();
    await delay(TIMINGS.messageDuration);
    if (!alive()) return;
    hideBubble();
    placeHome();
    activeRef.current = false;
  };

  // Dev-/Automations-Standard (?chris=poc): EV-012-Demo mit festem Zielanker.
  const DEV_SPEC = {
    testIdDesktop: TARGET_TESTID,
    testIdMobile: TARGET_TESTID,
    mode: "perch",
    message: MESSAGE_TEXT,
  };

  const start = (spec = DEV_SPEC) => {
    stop();
    computeSize();
    const testId =
      (window.innerWidth >= SIZES.breakpointLg ? spec.testIdDesktop : spec.testIdMobile) ||
      spec.testIdDesktop;
    targetTestIdRef.current = testId;
    modeRef.current = spec.mode || "perch";
    onShownRef.current = spec.onShown || null;
    setMessage(spec.message || MESSAGE_TEXT);
    // Mobile: horizontal scrollbaren Zielanker ohne Layoutänderung sichtbar machen.
    // (Scroll-Abbruch wird für dieses programmatische Scroll kurz unterdrückt.)
    if (spec.scrollIntoView) scrollTargetIntoView(testId);
    if (!getTargetRect()) {
      placeHome();
      return;
    }
    const reduced =
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const myId = runIdRef.current;
    if (reduced) runReduced(myId);
    else runFull(myId);
  };

  // ---- C4 „Auf Abruf" (kontrolliertes, additives Re-Open; keine C3-Semantikänderung) ----
  const closeC4 = () => {
    c4OpenRef.current = false;
    setC4Open(false);
    setC4Loading(false);
    setC4Error(false);
    setC4Data(null);
  };

  // Chris darf NUR im echten Desktop-Ruhezustand bewusst angeklickt werden.
  const handleChrisClick = async () => {
    if (c4OpenRef.current) return; // schon offen
    if (activeRef.current) return; // C3 läuft → Vorrang (C4-BE-005)
    if (phaseRef.current !== "home_sit") return; // nur Ruhezustand
    if (window.innerWidth < SIZES.breakpointLg) return; // Desktop-only (Scope)
    const p = pointRef.current;
    setC4Pos({
      left: Math.min(Math.max(Math.round(p.x) - 150, 12), window.innerWidth - 324),
      top: Math.round(p.y) + 44,
    });
    c4OpenRef.current = true;
    setC4Open(true);
    setC4Loading(true);
    setC4Error(false);
    setC4Data(null);
    try {
      const data = await api.getChrisSummary();
      if (!c4OpenRef.current) return;
      setC4Data(data);
    } catch (_) {
      if (!c4OpenRef.current) return;
      setC4Error(true);
    } finally {
      if (c4OpenRef.current) setC4Loading(false);
    }
  };

  // „Zeig's mir": bestehende C3-Bewegung wiederverwenden — ABER QUITTUNGSFREI.
  // KEIN onShown im Spec → onShownRef bleibt null → fireShown() ist No-Op →
  // KEIN markChrisShown / KEIN /chris/shown / KEIN Cooldown-Write (C4-BE-004).
  const handleZeigsMir = (key, count) => {
    const cfg = CHRIS_EVENTS.find((e) => e.key === key);
    if (!cfg) return;
    closeC4();
    start({
      testIdDesktop: cfg.testIdDesktop,
      testIdMobile: cfg.testIdMobile,
      mode: cfg.mode,
      scrollIntoView: cfg.scrollIntoView,
      message: c4NowText(key, count),
    });
  };

  // Initiale Home-Positionierung (mit kurzer Retry-Schleife, bis das Logo im DOM ist).
  useLayoutEffect(() => {
    mountedRef.current = true;
    computeSize();
    let tries = 0;
    const tryPlace = () => {
      if (!mountedRef.current) return;
      if (placeHome()) return;
      if (tries++ < 30) requestAnimationFrame(tryPlace);
    };
    tryPlace();
    return () => {
      mountedRef.current = false;
      stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sprechblase neu vermessen & klemmen, sobald sie sichtbar wird.
  useLayoutEffect(() => {
    if (bubble) positionBubble();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bubble]);

  // Dev-/Test-Trigger: NUR mit ?chris=poc sichtbar + window.__chris für Automation.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    setDevTrigger(params.get("chris") === "poc");
    window.__chris = {
      start,
      stop,
      getState: () => ({
        pose: poseKeyRef.current,
        phase: phaseRef.current,
        facing: facingRef.current,
        x: Math.round(pointRef.current.x),
        y: Math.round(pointRef.current.y),
        active: activeRef.current,
      }),
    };
    return () => {
      try {
        delete window.__chris;
      } catch (_) {
        /* noop */
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Scroll/Resize/Orientation: idle → mitführen; aktive Sequenz → sauberer Abbruch.
  useEffect(() => {
    let scheduled = false;
    const onScroll = () => {
      // C3-Fix: Chris' eigenes programmatisches scrollIntoView (mobiler Nav-Streifen)
      // nicht als Nutzer-Scroll werten → laufende Sequenz nicht abbrechen.
      if (performance.now() < scrollSuppressUntilRef.current) return;
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(() => {
        scheduled = false;
        if (activeRef.current) finishReset();
        else placeHome();
      });
    };
    const onResize = () => {
      computeSize();
      const desk = window.innerWidth >= SIZES.breakpointLg;
      setIsDesktop(desk);
      if (!desk && c4OpenRef.current) closeC4(); // C4 ist Desktop-only
      if (activeRef.current) finishReset();
      else placeHome();
    };
    window.addEventListener("scroll", onScroll, { passive: true, capture: true });
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);
    return () => {
      window.removeEventListener("scroll", onScroll, { capture: true });
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Route-Wechsel innerhalb /unternehmer: Sequenz stoppen, sauber am Logo neu setzen.
  useEffect(() => {
    stop();
    closeC4(); // C4-Summary bei Routenwechsel schließen
    const id = setTimeout(() => placeHome(), 60);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Preload der gesperrten Assets (einmalig; kein Neuladen pro Frame).
  useEffect(() => {
    POSE_SLUGS.forEach((slug) => {
      const img = new Image();
      img.src = srcFor(slug);
    });
  }, []);

  // C3 · AUSLÖSER-QUEUE — nur auf dem Dashboard. Höchstpriorisierten AKTIVEN
  // Trigger wählen, der NICHT im Cooldown ist, und EINMAL pro Dashboard-Eintritt
  // anzeigen. Rückkehr ins Dashboard = neue Bewertung (verhaltensgesteuert).
  useEffect(() => {
    if (location.pathname !== TRIGGER.dashboardPath) return undefined;
    let cancelled = false;
    const timers = [];
    const evaluate = async () => {
      let data;
      try {
        data = await api.getChrisTriggers();
      } catch (_) {
        return; // READ fail-closed: im Zweifel schweigt Chris.
      }
      if (cancelled || !data || data.chris_enabled === false) return;
      const now = Date.now();
      const cooldowns = data.cooldowns || {};
      const events = data.events || {};
      const chosen = CHRIS_EVENTS.find((cfg) => {
        const ev = events[cfg.key];
        if (!ev || !ev.active) return false;
        const last = cooldowns[cfg.key];
        if (!last) return true;
        const ms = Date.parse(last);
        if (Number.isNaN(ms)) return false; // unlesbar → fail-closed (schweigen)
        return now - ms >= cfg.cooldownDays * 24 * 3600 * 1000;
      });
      if (!chosen || cancelled) return;
      const count = (events[chosen.key] && events[chosen.key].count) || 1;
      const spec = {
        testIdDesktop: chosen.testIdDesktop,
        testIdMobile: chosen.testIdMobile,
        mode: chosen.mode,
        scrollIntoView: chosen.scrollIntoView,
        message: chosen.message(count),
        onShown: () => {
          api.markChrisShown(chosen.key).catch(() => {
            /* WRITE fail-open: Anwendung nie blockieren. */
          });
        },
      };
      // Zielanker evtl. noch nicht gerendert → wenige Retries, dann aufgeben.
      let tries = 0;
      const attempt = () => {
        if (cancelled || activeRef.current) return;
        const testId =
          (window.innerWidth >= SIZES.breakpointLg ? spec.testIdDesktop : spec.testIdMobile) ||
          spec.testIdDesktop;
        if (spec.scrollIntoView) scrollTargetIntoView(testId);
        if (rectOf(testId)) {
          if (c4OpenRef.current) closeC4(); // C3 hat Vorrang (C4-BE-005)
          start(spec);
          return;
        }
        if (tries++ < TRIGGER.maxRetries) {
          const id = setTimeout(attempt, TRIGGER.retryMs);
          timers.push(id);
        }
      };
      attempt();
    };
    const settle = setTimeout(evaluate, TRIGGER.settleMs);
    timers.push(settle);
    return () => {
      cancelled = true;
      timers.forEach(clearTimeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const c4Clickable = isDesktop && pose === "01_chris_home_sit" && !c4Open;

  return (
    <div
      ref={overlayRef}
      data-testid="chris-overlay"
      aria-hidden="true"
      style={{
        position: "fixed",
        inset: 0,
        overflow: "hidden",
        pointerEvents: "none",
        zIndex: Z_INDEX,
      }}
    >
      <div
        ref={containerRef}
        data-testid="chris-click-target"
        onClick={handleChrisClick}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          willChange: "transform",
          pointerEvents: c4Clickable ? "auto" : "none",
          cursor: c4Clickable ? "pointer" : "default",
        }}
      >
        <img
          ref={imgRef}
          src={srcFor(pose)}
          alt=""
          draggable={false}
          data-testid="chris-figure"
          data-pose={pose}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "contain",
            userSelect: "none",
            transform: `scaleX(${facing})`,
          }}
        />
      </div>

      {bubble && (
        <div
          ref={bubbleRef}
          data-testid="chris-message"
          style={{
            position: "absolute",
            left: bubblePos.left,
            top: bubblePos.top,
            maxWidth: `min(${BUBBLE.maxWidth}px, calc(100vw - ${2 * TARGET.margin}px))`,
            background: "#ffffff",
            color: "#1a3f6f",
            border: `${BUBBLE.borderWidth}px solid #1a3f6f`,
            borderRadius: BUBBLE.radius,
            boxShadow: "0 8px 24px rgba(15,23,42,0.18)",
            padding: "12px 16px",
            fontSize: "clamp(15px, 1.2vw, 18px)",
            fontWeight: 600,
            lineHeight: 1.3,
            textAlign: "center",
            whiteSpace: "normal",
          }}
        >
          {message}
          {/* Sprechblasen-Schwanz (CSS-Dreieck): Navy-Kontur außen, Weiß innen,
              zeigt Richtung Chris' Kopf. Kein Bildasset nötig. */}
          {(() => {
            const [outer, inner] = tailPair();
            return (
              <>
                <span aria-hidden="true" style={outer} />
                <span aria-hidden="true" style={inner} />
              </>
            );
          })()}
        </div>
      )}

      {c4Open && (
        <div
          data-testid="chris-ask-panel"
          style={{
            position: "fixed",
            left: c4Pos.left,
            top: c4Pos.top,
            width: 312,
            maxWidth: "calc(100vw - 24px)",
            pointerEvents: "auto",
            background: "#ffffff",
            color: "#1a3f6f",
            border: "2px solid #1a3f6f",
            borderRadius: 16,
            boxShadow: "0 12px 32px rgba(15,23,42,0.22)",
            padding: "14px 16px",
            zIndex: Z_INDEX + 1,
            fontSize: 14,
            lineHeight: 1.35,
          }}
        >
          <button
            type="button"
            data-testid="chris-ask-close"
            onClick={closeC4}
            aria-label="Schließen"
            style={{
              position: "absolute",
              top: 8,
              right: 8,
              border: "none",
              background: "transparent",
              color: "#1a3f6f",
              cursor: "pointer",
              lineHeight: 0,
              padding: 4,
            }}
          >
            <X size={16} />
          </button>

          {c4Loading && (
            <div data-testid="chris-ask-loading" style={{ fontWeight: 600 }}>
              Einen Moment …
            </div>
          )}

          {!c4Loading && c4Error && (
            <div data-testid="chris-ask-error" style={{ fontWeight: 600 }}>
              Ich kann gerade nicht nachsehen.
            </div>
          )}

          {!c4Loading && !c4Error && c4Data && (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {c4Data.last && (
                <div
                  data-testid="chris-ask-last"
                  style={{ display: "flex", gap: 8, alignItems: "flex-start" }}
                >
                  <CheckCircle2 size={18} style={{ color: "#2e7d32", flexShrink: 0, marginTop: 1 }} />
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 12, textTransform: "uppercase", letterSpacing: 0.3, opacity: 0.7 }}>
                      Zuletzt erledigt
                    </div>
                    <div>
                      {(C4_AREA_LABEL[c4Data.last.area] || c4Data.last.area)} · {c4RelPast(c4Data.last.age_days)}
                    </div>
                  </div>
                </div>
              )}

              {c4Data.now && c4Data.now.length > 0 ? (
                <div data-testid="chris-ask-now" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <AlertCircle size={18} style={{ color: "#b45309", flexShrink: 0 }} />
                    <div style={{ fontWeight: 700, fontSize: 12, textTransform: "uppercase", letterSpacing: 0.3, opacity: 0.7 }}>
                      Jetzt wichtig
                    </div>
                  </div>
                  {c4Data.now.map((it) => (
                    <div
                      key={it.key}
                      data-testid={`chris-ask-now-${it.key}`}
                      style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, paddingLeft: 26 }}
                    >
                      <span>{c4NowText(it.key, it.count)}</span>
                      <button
                        type="button"
                        data-testid={`chris-ask-zeigsmir-${it.key}`}
                        onClick={() => handleZeigsMir(it.key, it.count)}
                        style={{
                          flexShrink: 0,
                          border: "1.5px solid #1a3f6f",
                          background: "#1a3f6f",
                          color: "#fff",
                          borderRadius: 999,
                          padding: "4px 12px",
                          fontSize: 12,
                          fontWeight: 700,
                          cursor: "pointer",
                          whiteSpace: "nowrap",
                        }}
                      >
                        Zeig's mir
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div data-testid="chris-ask-clear" style={{ display: "flex", gap: 8, alignItems: "center", fontWeight: 600 }}>
                  <CheckCircle2 size={18} style={{ color: "#2e7d32", flexShrink: 0 }} />
                  <span>
                    {c4Data.state === "clear_with_next"
                      ? "Alles erledigt."
                      : c4Data.state === "partial_unknown"
                      ? "Ich kann gerade nicht alles nachsehen."
                      : "Aktuell wartet nichts auf Sie."}
                  </span>
                </div>
              )}

              {c4Data.next && (
                <div data-testid="chris-ask-next" style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
                  <Clock size={18} style={{ color: "#1a3f6f", flexShrink: 0, marginTop: 1, opacity: 0.8 }} />
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 12, textTransform: "uppercase", letterSpacing: 0.3, opacity: 0.7 }}>
                      Als Nächstes
                    </div>
                    <div>
                      {(C4_AREA_LABEL[c4Data.next.area] || c4Data.next.area)} · {c4RelFuture(c4Data.next.in_days)}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {devTrigger && (
        <button
          type="button"
          data-testid="chris-dev-trigger"
          onClick={() => start()}
          style={{
            position: "fixed",
            right: 16,
            bottom: 16,
            pointerEvents: "auto",
            zIndex: Z_INDEX + 1,
            background: "#1a3f6f",
            color: "#fff",
            border: "none",
            borderRadius: 999,
            padding: "10px 16px",
            fontSize: 13,
            fontWeight: 700,
            boxShadow: "0 6px 20px rgba(15,23,42,0.25)",
            cursor: "pointer",
          }}
        >
          ▶ Chris (C1)
        </button>
      )}
    </div>
  );
}
